import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import AppImage from '../../../components/AppImage';

const ProfileHeader = ({ userData, editMode, onEditToggle, onProfileUpdate }) => {
  const [formData, setFormData] = useState({
    name: userData?.name || '',
    bio: userData?.bio || '',
    skillLevel: userData?.skillLevel || '',
    preferredPositions: userData?.preferredPositions || []
  });

  const skillLevels = [
    { value: 'iniciante', label: 'Iniciante' },
    { value: 'intermediario', label: 'Intermediário' },
    { value: 'avancado', label: 'Avançado' },
    { value: 'profissional', label: 'Profissional' }
  ];

  const positions = [
    { value: 'goleiro', label: 'Goleiro' },
    { value: 'zagueiro', label: 'Zagueiro' },
    { value: 'lateral', label: 'Lateral' },
    { value: 'volante', label: 'Volante' },
    { value: 'meio-campo', label: 'Meio-campo' },
    { value: 'atacante', label: 'Atacante' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePositionToggle = (position) => {
    setFormData(prev => ({
      ...prev,
      preferredPositions: prev?.preferredPositions?.includes(position)
        ? prev?.preferredPositions?.filter(p => p !== position)
        : [...(prev?.preferredPositions || []), position]
    }));
  };

  const handleSave = () => {
    onProfileUpdate(formData);
  };

  const handleCancel = () => {
    setFormData({
      name: userData?.name || '',
      bio: userData?.bio || '',
      skillLevel: userData?.skillLevel || '',
      preferredPositions: userData?.preferredPositions || []
    });
    onEditToggle();
  };

  const getReputationColor = (reputation) => {
    if (reputation >= 4.5) return 'text-success';
    if (reputation >= 3.5) return 'text-warning';
    return 'text-destructive';
  };

  const getSkillLevelBadgeColor = (skillLevel) => {
    switch (skillLevel?.toLowerCase()) {
      case 'profissional':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'avancado':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'intermediario':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-green-100 text-green-700 border-green-200';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Cover/Header Background */}
      <div className="h-32 bg-gradient-to-r from-primary to-primary/70 relative">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-4 right-4">
          {!editMode ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={onEditToggle}
              iconName="Edit2"
              iconPosition="left"
              iconSize={16}
              className="bg-white/90 hover:bg-white"
            >
              Editar
            </Button>
          ) : (
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCancel}
                className="bg-white/90 hover:bg-white"
              >
                Cancelar
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                iconName="Check"
                iconPosition="left"
                iconSize={16}
                className="bg-success text-white hover:bg-success/90"
              >
                Salvar
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Profile Content */}
      <div className="px-6 pb-6">
        {/* Avatar and Basic Info */}
        <div className="flex items-start space-x-4 -mt-16 relative z-10">
          <div className="relative">
            <AppImage
              src={userData?.avatar}
              alt={userData?.avatarAlt}
              className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover"
            />
            {editMode && (
              <button className="absolute bottom-0 right-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-md hover:bg-primary/90 transition-standard">
                <Icon name="Camera" size={16} />
              </button>
            )}
          </div>
          
          <div className="flex-1 pt-8">
            {editMode ? (
              <div className="space-y-3">
                <Input
                  placeholder="Seu nome"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  className="font-heading font-bold text-lg"
                />
              </div>
            ) : (
              <div>
                <h1 className="text-2xl font-heading font-bold text-card-foreground">
                  {userData?.name}
                </h1>
                <div className="flex items-center space-x-2 mt-1">
                  <Icon name="MapPin" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">{userData?.location}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        <div className="mt-4">
          {editMode ? (
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Biografia
              </label>
              <textarea
                placeholder="Conte um pouco sobre você..."
                value={formData?.bio}
                onChange={(e) => handleInputChange('bio', e?.target?.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-input rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
            </div>
          ) : (
            userData?.bio && (
              <p className="text-muted-foreground text-sm leading-relaxed">
                {userData?.bio}
              </p>
            )
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-4 border-t border-border">
          <div className="text-center">
            <div className="text-2xl font-bold text-card-foreground">
              {userData?.totalMatches}
            </div>
            <div className="text-xs text-muted-foreground">Partidas</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-success">
              {userData?.wins}
            </div>
            <div className="text-xs text-muted-foreground">Vitórias</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">
              {userData?.goals}
            </div>
            <div className="text-xs text-muted-foreground">Gols</div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1">
              <span className={`text-xl font-bold ${getReputationColor(userData?.reputation)}`}>
                {userData?.reputation?.toFixed(1)}
              </span>
              <Icon name="Star" size={16} className={getReputationColor(userData?.reputation)} />
            </div>
            <div className="text-xs text-muted-foreground">Reputação</div>
          </div>
        </div>

        {/* Skill Level and Positions */}
        <div className="mt-6 space-y-4">
          {editMode ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Nível de Habilidade
                </label>
                <Select
                  options={skillLevels}
                  value={formData?.skillLevel}
                  onChange={(value) => handleInputChange('skillLevel', value)}
                  placeholder="Selecione seu nível"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Posições Preferidas
                </label>
                <div className="flex flex-wrap gap-2">
                  {positions?.map((position) => (
                    <button
                      key={position?.value}
                      onClick={() => handlePositionToggle(position?.label)}
                      className={`px-3 py-1 text-xs rounded-full border transition-standard ${
                        formData?.preferredPositions?.includes(position?.label)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-muted text-muted-foreground border-muted hover:border-primary'
                      }`}
                    >
                      {position?.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <span className={`px-3 py-1 text-xs rounded-full border ${getSkillLevelBadgeColor(userData?.skillLevel)}`}>
                {userData?.skillLevel}
              </span>
              {userData?.preferredPositions?.map((position) => (
                <span
                  key={position}
                  className="px-3 py-1 text-xs rounded-full bg-muted text-muted-foreground border border-muted"
                >
                  {position}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Additional Info */}
        {!editMode && (
          <div className="mt-6 pt-4 border-t border-border">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground">
                  Membro desde {new Date(userData?.joinDate)?.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground">
                  {userData?.badges} badges conquistados
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="Zap" size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground">
                  {userData?.streak} vitórias seguidas
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="Target" size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground">
                  {userData?.assists} assistências
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileHeader;